import React from 'react';
import PropTypes from 'prop-types';

import Arrow from '../components/arrow';
import Card from '../components/card';
import ProgressDots from '../components/progress-dots';
import withDevice from '../components/withDevice';

import {
    CarouselProvider,
    Slider,
    Slide,
    WithStore,
} from 'pure-react-carousel';

export const CardSliderArrow = WithStore(
    class extends React.Component {
        static propTypes = {
            direction: PropTypes.oneOf(['prev', 'next']).isRequired,
            className: PropTypes.string,
        };

        static defaultProps = {
            className: '',
        };

        prev() {
            const {
                carouselStore,
                currentSlide,
                step,
                infinite,
                visibleSlides,
                totalSlides,
            } = this.props;

            const maxSlide = totalSlides - visibleSlides;

            let newCurrentSlide = Math.max(currentSlide - step, 0);

            if (infinite) {
                const isOnFirstSlide = currentSlide === 0;
                newCurrentSlide = isOnFirstSlide ? maxSlide : newCurrentSlide;
            }

            carouselStore.setStoreState({
                currentSlide: newCurrentSlide,
            });
        }

        next() {
            const {
                currentSlide,
                step,
                carouselStore,
                infinite,
                totalSlides,
                visibleSlides,
            } = this.props;

            const maxSlide = totalSlides - visibleSlides;
            const nextSlide = step + currentSlide;

            let newCurrentSlide = Math.min(nextSlide, maxSlide);

            if (infinite) {
                const isOnLastSlide = maxSlide === currentSlide;
                newCurrentSlide = isOnLastSlide ? 0 : newCurrentSlide;
            }

            carouselStore.setStoreState({
                currentSlide: newCurrentSlide,
            });
        }

        render() {
            const {
                direction,
                className,
                currentSlide,
                step,
                infinite,
                totalSlides,
                visibleSlides,
                carouselStore,
                ...safeProps
            } = this.props;
            return (
                <Arrow
                    {...safeProps}
                    className={className}
                    direction={direction}
                    onClick={(e) => {
                        e.preventDefault();
                        if (direction === 'prev') {
                            this.prev();
                        } else {
                            this.next();
                        }
                    }}
                />
            );
        }
    },
    (state) => ({
        currentSlide: state.currentSlide,
        step: state.step,
        infinite: state.infinite,
        totalSlides: state.totalSlides,
        visibleSlides: state.visibleSlides,
    }),
);

export const CardSliderDots = WithStore(
    class extends React.Component {
        static propTypes = {
            className: PropTypes.string,
            total: PropTypes.number.isRequired,
        };

        static defaultProps = {
            className: '',
        };

        onClick = (page) => {
            const { perPage } = this.props;
            this.props.carouselStore.setStoreState({
                currentSlide: page * perPage,
            });
        };

        render() {
            const {
                active,
                total,
                className,
                perPage,
                carouselStore,
                ...safeProps
            } = this.props;
            return (
                <ProgressDots
                    {...safeProps}
                    className={className}
                    label="Page"
                    current={active}
                    total={total}
                    onDotClick={this.onClick}
                />
            );
        }
    },
    (state) => ({
        active: Math.ceil(state.currentSlide / state.visibleSlides),
        perPage: state.visibleSlides,
    }),
);

class CardSlider extends React.Component {
    static propTypes = {
        items: PropTypes.array,
        cardProps: PropTypes.func,
        loading: PropTypes.bool,
        showDots: PropTypes.bool,
        dotProps: PropTypes.object,
        showArrows: PropTypes.bool,
        arrowProps: PropTypes.object,
    };

    static defaultProps = {
        items: [],
        cardProps: (item) => item,
        loading: false,
        showDots: true,
        dotProps: {},
        showArrows: true,
        arrowProps: {},
    };

    state = {
        perPage: 4,
    };

    componentDidMount() {
        this.updateDevice();
    }

    componentDidUpdate(prevProps) {
        const { device } = this.props;
        if (device.current !== prevProps.device.current) {
            this.updateDevice();
        }
    }

    updateDevice() {
        const { device } = this.props;
        let perPage;
        switch (device.current) {
            case 'xlg':
                perPage = 4;
                break;
            case 'lg':
                perPage = 3;
                break;
        }

        this.setState({ perPage });
    }

    render() {
        const {
            items,
            cardProps,
            loading,
            device,
            showDots,
            dotProps,
            showArrows,
            arrowProps,
        } = this.props;
        const { perPage } = this.state;

        const numPages = Math.ceil(items.length / perPage);

        return (
            <>
                {device.isDesktop && (
                    <CarouselProvider
                        className="card-slider"
                        naturalSlideWidth={320}
                        isIntrinsicHeight={true}
                        totalSlides={items.length}
                        visibleSlides={perPage}
                        infinite={true}
                        step={perPage}
                        dragStep={perPage}
                        hasMasterSpinner={loading}>
                        <Slider className="card-slider__slider">
                            {items.map((item, i) => {
                                const { className, ...otherProps } = cardProps(
                                    item,
                                );
                                return (
                                    <Slide
                                        key={i}
                                        className="card-slider__slide">
                                        <Card
                                            className={`card-slider__card ${className}`}
                                            {...otherProps}
                                        />
                                    </Slide>
                                );
                            })}
                        </Slider>
                        {numPages > 1 && (showDots || showArrows) && (
                            <div className="card-slider__controls">
                                {showArrows && (
                                    <CardSliderArrow
                                        {...arrowProps}
                                        className="card-slider__arrow card-slider__arrow--prev"
                                        direction="prev"
                                    />
                                )}
                                {showDots && (
                                    <CardSliderDots
                                        {...dotProps}
                                        total={numPages}
                                    />
                                )}
                                {showArrows && (
                                    <CardSliderArrow
                                        {...arrowProps}
                                        className="card-slider__arrow card-slider__arrow--next"
                                        direction="next"
                                    />
                                )}
                            </div>
                        )}
                    </CarouselProvider>
                )}

                {(device.isMobile || device.isTablet) && (
                    <div className="card-slider__scroller">
                        {items.map((item, i) => {
                            const { className, ...otherProps } = cardProps(
                                item,
                            );
                            return (
                                <Card
                                    key={i}
                                    className={`card-slider__card ${className}`}
                                    {...otherProps}
                                />
                            );
                        })}
                        <div className="card-slider__dummy" aria-hidden>
                            &nbsp;
                        </div>
                    </div>
                )}
            </>
        );
    }
}

export default withDevice(CardSlider);
